import React, { useState, useRef, useEffect } from "react";
import { OtpInputProps } from "../../service/interfaces/Components";


const OtpInput: React.FC<OtpInputProps> = ({
  length,
  onComplete,
  errors = {},
  placeholder = "",
  disabled = false,
  className = "",
  value = "", // default to empty string
  onChange = () => {},
}) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
  const inputsRef = useRef<HTMLInputElement[]>([]);

  const handleChange = (value: string, index: number) => {
    if (!/^[\d]*$/.test(value)) return; // Allow only numeric input
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Focus next input if the current box is filled
    if (value && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }

    // If OTP is complete, call onComplete
    if (newOtp.join("".trim()).length === length) {
      onComplete(newOtp.join(""));
    }
  };

  const handleBackspace = (index: number) => {
    const newOtp = [...otp];
    newOtp[index] = "";
    setOtp(newOtp);

    // Focus previous input on backspace if it's empty
    if (index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  useEffect(() => {
    // If value is empty and we want to reset the input
    if (value === "" && otp.some(digit => digit !== "")) {
      setOtp(Array(length).fill(""));
    }
  }, [value, length]);

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, length);
    if (!/^\d+$/.test(pastedData)) return; // Allow only numeric paste

    const newOtp = pastedData.split("");
    setOtp(newOtp);

    // Call onComplete if pasted OTP is complete
    if (newOtp.length === length) {
      onComplete(newOtp.join(""));
    }

    // Focus the last input field
    const lastFilledIndex = Math.min(newOtp.length - 1, length - 1);
    inputsRef.current[lastFilledIndex]?.focus();
  };

  return (
    <div
      className={`otp-input-container ${className}`}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
        marginBottom: "1rem",
        width: "100%",
      }}
    >
      {otp.map((digit, index) => (
        <div key={index} style={{ position: "relative" }}>
          <input
            type="text"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => {
              if (e.key === "Backspace" && !otp[index]) {
                handleBackspace(index);
              }
            }}
            onPaste={handlePaste}
            ref={(el) => (inputsRef.current[index] = el!)}
            disabled={disabled}
            placeholder={placeholder}
            className={`otp-input placeholder-[#8897AD] font-[400] text-[#333] ${
              errors[index]?.message ? "border-red-500" : "border-gray-300"
            }`}
            style={{
              width: "40px",
              height: "50px",
              textAlign: "center",
              fontSize: "18px",
              border: `1px solid ${errors[index]?.message ? "#EF4444" : "#D1D5DB"}`,
              borderRadius: "8px",
              backgroundColor: "#F7FBFF",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              outline: "none",
              transition: "border-color 0.2s ease",
            }}
          />
          {errors[index]?.message && (
            <p
              style={{
                position: "absolute",
                bottom: "-20px",
                fontSize: "12px",
                color: "#EF4444",
              }}
            >
              {errors[index]?.message}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default OtpInput;